import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, suppliersAPI, sitesAPI, supplierProductsAPI, industrialProductsAPI, exportAPI, transfersAPI, downloadAuthFile } from '@/lib/api';
import { FlaskConical, Trash2, GitMerge, ClipboardList, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import OrdersDialogs from './OrdersDialogs';
import InvoiceReceptionDialog from './dialogs/InvoiceReceptionDialog';
import ConsolidatedManualReceptionDialog from './dialogs/ConsolidatedManualReceptionDialog';
import OrderFormsDialog from './dialogs/OrderFormsDialog';
import OrderFormPhotoImportDialog from './dialogs/OrderFormPhotoImportDialog';
import TransferInViewDialog from './dialogs/TransferInViewDialog';
import CarrefourImportDialog from '@/components/CarrefourImportDialog';
import InvoiceImportDialog from '@/components/InvoiceImportDialog';
import OrdersFilterBar from './components/OrdersFilterBar';
import OrdersListTable from './components/OrdersListTable';
import OrdersTrashDialog from './OrdersTrashDialog';
import SupplierShortcutEditor from './dialogs/SupplierShortcutEditor';
import {
    formatCurrency,
    supplierShortcutLabel,
    getUserAllowedCompanyFlags,
    filterByUserCompanies,
    COMPANY_FLAG_KEYS,
} from '@/lib/utils';
import api from '@/lib/api';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    ShoppingCart,
    Plus,
    Download,
    PackageCheck,
    Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function OrdersTab() {
    const { hasPermission, user } = useAuth();
    const navigate = useNavigate();
    const [openedFromDrawer, setOpenedFromDrawer] = useState(false);
    const [carrefourDialog, setCarrefourDialog] = useState(false);
    const [invoiceImportDialog, setInvoiceImportDialog] = useState(false);
    const [orders, setOrders] = useState([]);
    const [manualReceptions, setManualReceptions] = useState([]);
    const [validatedTransfers, setValidatedTransfers] = useState([]);
    const [viewTransferItem, setViewTransferItem] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [sites, setSites] = useState([]);
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [industrialProducts, setIndustrialProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSupplier, setFilterSupplier] = useState('all');
    const [filterSite, setFilterSite] = useState('all');
    const [filterCompany, setFilterCompany] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPeriod, setFilterPeriod] = useState('month');
    const [customPeriodStart, setCustomPeriodStart] = useState('');
    const [customPeriodEnd, setCustomPeriodEnd] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'order', 'manual'
    // Exclusions temporaires (appui long sur un chip) — non persistées
    const [excludedSites, setExcludedSites] = useState([]);
    const [excludedCompanies, setExcludedCompanies] = useState([]);

    // Supplier shortcut bar: user-editable list of quick filters (persisted per user/browser).
    const DEFAULT_SUPPLIER_SHORTCUTS = [
        'Vodelys',
        'Pro à Pro Surgelés',
        'MG Store',
        'Asie Caraibes',
        'Le Moulin',
        'S.A.P.Y.',
    ];
    const [supplierShortcutNames, setSupplierShortcutNames] = useState(() => {
        try {
            const raw = localStorage.getItem('ordersTab_supplierShortcuts');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch { /* ignore */ }
        return DEFAULT_SUPPLIER_SHORTCUTS;
    });
    const [showShortcutEditor, setShowShortcutEditor] = useState(false);
    const shortcutsSyncedRef = useRef(false);
    useEffect(() => {
        api.get('/auth/prefs').then(res => {
            const server = res.data?.orders_supplier_shortcuts;
            if (Array.isArray(server) && server.length) {
                setSupplierShortcutNames(server);
            } else {
                api.put('/auth/prefs', { orders_supplier_shortcuts: supplierShortcutNames }).catch(() => {});
            }
            shortcutsSyncedRef.current = true;
        }).catch(() => { shortcutsSyncedRef.current = true; });
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        try {
            localStorage.setItem(
                'ordersTab_supplierShortcuts',
                JSON.stringify(supplierShortcutNames)
            );
        } catch { /* ignore */ }
        if (shortcutsSyncedRef.current) {
            api.put('/auth/prefs', { orders_supplier_shortcuts: supplierShortcutNames }).catch(() => {});
        }
    }, [supplierShortcutNames]);

    // Toggle: hide products already added to current order/reception when picking a new product.
    // ON (default) = filter out products already in the list. OFF = show all supplier products.
    // Persisted globally; applies to: Nouvelle commande, Modifier commande, Nouvelle réception, Ajout extra réception.
    const [hideAddedProducts, setHideAddedProducts] = useState(() => {
        try {
            const v = localStorage.getItem('ordersTab_hideAddedProducts');
            // Default ON when no preference stored yet
            return v === null ? true : v === '1';
        } catch { return true; }
    });
    useEffect(() => {
        try {
            localStorage.setItem('ordersTab_hideAddedProducts', hideAddedProducts ? '1' : '0');
        } catch { /* ignore */ }
    }, [hideAddedProducts]);

    // Order-creation mode (admin/supervisor only): 'normal' shows the regular
    // commandable product catalogue; 'non_commandable' shows the deactivated
    // ones (used for one-off / out-of-stock exceptional orders). Reset to
    // 'normal' on every dialog reopening to avoid an admin forgetting they're
    // in exceptional mode for the next order. NOT persisted.
    const [orderableMode, setOrderableMode] = useState('normal');
    
    // Permissions
    const canCreate = hasPermission('orders_create');
    const canEdit = hasPermission('orders_edit');
    const canDelete = hasPermission('orders_delete');
    const canSendEmail = hasPermission('orders_send_email');
    const canSendWhatsApp = hasPermission('orders_send_whatsapp');
    const canCreateReception = hasPermission('receptions_create');
    const [consolidatedReceptionDialog, setConsolidatedReceptionDialog] = useState(false);
    const canExportExcel = hasPermission('orders_export_excel');
    const canImportInvoice = hasPermission('orders_import_invoice');
    
    // Filtrer les sites selon les sites autorisés de l'utilisateur
    const getAllowedSites = (allSites) => {
        if (!user?.allowed_sites || user.allowed_sites.length === 0) {
            return allSites; // Accès à tous les sites
        }
        return allSites.filter(site => user.allowed_sites.includes(site.id));
    };

    // Company → list of site name prefixes or exact names
    const COMPANY_MATCHERS = {
        'Fuji Express': (name) => name.startsWith('FE '),
        'Concept Lab': (name) => name === 'Concept Lab',
        'My Food': (name) => name === 'My Food',
        'Boucherie': (name) => name === 'Boucherie',
    };

    const getSiteIdsForCompany = (companyName) => {
        const matcher = COMPANY_MATCHERS[companyName];
        if (!matcher) return [];
        return sites.filter(s => matcher(s.name)).map(s => s.id);
    };

    const isSiteInCompany = (siteId, companyName) => {
        const ids = getSiteIdsForCompany(companyName);
        return ids.includes(siteId);
    };

    // Supplier shortcut helpers: fuzzy-resolve display names to actual supplier records.
    const normalizeStr = (s) => (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
    const resolveShortcutSupplier = (name) => {
        const n = normalizeStr(name);
        if (!n) return null;
        // Prefer exact normalized match, then startsWith, then includes
        let match = suppliers.find(s => normalizeStr(s.name) === n);
        if (!match) match = suppliers.find(s => normalizeStr(s.name).startsWith(n));
        if (!match) match = suppliers.find(s => normalizeStr(s.name).includes(n));
        return match || null;
    };

    // Period helpers
    const PERIOD_PRESETS = {
        all: { label: 'Tout', days: null },
        week: { label: 'Semaine', days: 7 },
        '2weeks': { label: '2 semaines', days: 14 },
        month: { label: 'Mois', days: 30 },
        '3months': { label: '3 mois', days: 90 },
        year: { label: '1 an', days: 365 },
    };

    const getPeriodRange = () => {
        if (filterPeriod === 'custom') {
            return { start: customPeriodStart || null, end: customPeriodEnd || null };
        }
        const preset = PERIOD_PRESETS[filterPeriod];
        if (!preset || !preset.days) return { start: null, end: null };
        const start = new Date();
        start.setDate(start.getDate() - preset.days);
        // No end bound: future-dated orders must stay visible in the list.
        return {
            start: start.toISOString().split('T')[0],
            end: null,
        };
    };

    const isInPeriod = (dateStr) => {
        if (!dateStr) return true;
        const { start, end } = getPeriodRange();
        if (!start && !end) return true;
        const datePart = dateStr.substring(0, 10); // YYYY-MM-DD
        if (start && datePart < start) return false;
        if (end && datePart > end) return false;
        return true;
    };
    
    // Dialogs
    const [createDialog, setCreateDialog] = useState(false);
    const [orderFormsDialog, setOrderFormsDialog] = useState(false);
    const [orderFormPhotoDialog, setOrderFormPhotoDialog] = useState(false);
    const [trashDialog, setTrashDialog] = useState(false);
    const [invoiceReceptionDialog, setInvoiceReceptionDialog] = useState(false);
    const [invoiceReceptionOrder, setInvoiceReceptionOrder] = useState(null);
    const [trashCount, setTrashCount] = useState(0);
    const [detailDialog, setDetailDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [receptionDialog, setReceptionDialog] = useState(false);
    const [manualReceptionDialog, setManualReceptionDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderToDelete, setOrderToDelete] = useState(null);
    
    // Edit order mode
    const [editOrderMode, setEditOrderMode] = useState(false);
    const [editOrderItems, setEditOrderItems] = useState([]);
    const [editOrderNotes, setEditOrderNotes] = useState('');
    const [editOrderDate, setEditOrderDate] = useState('');
    const [editOrderDocNumber, setEditOrderDocNumber] = useState('');
    const [addItemToOrder, setAddItemToOrder] = useState({ supplier_product_id: '', quantity: 1, unit_price: 0, order_mode: 'packaging' });
    const [addReceptionItem, setAddReceptionItem] = useState({ 
        supplier_product_id: '', 
        quantity: 1, 
        unit_price: 0,
        date: new Date().toISOString().split('T')[0],
        document_number: ''
    });
    
    // Reception history
    const [receptionHistory, setReceptionHistory] = useState([]);
    const [editingReception, setEditingReception] = useState(null);
    const [editReceptionDialog, setEditReceptionDialog] = useState(false);
    const [emailTestMode, setEmailTestMode] = useState(false);
    
    // Reception form
    const [receptionItems, setReceptionItems] = useState([]);
    const [receptionSaving, setReceptionSaving] = useState(false);
    const [manualReceptionSaving, setManualReceptionSaving] = useState(false);
    const [closeRemainingQuantities, setCloseRemainingQuantities] = useState(false);
    const [documentNumber, setDocumentNumber] = useState('');
    const [receptionDate, setReceptionDate] = useState(new Date().toISOString().split('T')[0]);
    // Extra product to add during reception (not in the original order)
    const [extraReceptionItem, setExtraReceptionItem] = useState({ supplier_product_id: '', quantity: '', unit_price: '' });

    // Replacement dialog state: when supplier delivered a different product than ordered
    const [replacementDialog, setReplacementDialog] = useState({ open: false, originalIndex: null });
    const [replacementForm, setReplacementForm] = useState({ supplier_product_id: '', quantity: '', unit_price: '' });
    
    // Manual reception form
    const [manualReceptionForm, setManualReceptionForm] = useState({
        supplier_id: '',
        site_id: '',
        reception_date: new Date().toISOString().split('T')[0],
        notes: '',
        items: []
    });
    const [manualReceptionItem, setManualReceptionItem] = useState({
        supplier_product_id: '',
        quantity: 1,
        unit_price: ''
    });
    const [selectedManualReception, setSelectedManualReception] = useState(null);
    const [manualReceptionDetailDialog, setManualReceptionDetailDialog] = useState(false);
    const [manualReceptionToDelete, setManualReceptionToDelete] = useState(null);
    const [deleteManualReceptionDialog, setDeleteManualReceptionDialog] = useState(false);
    const [editingManualReception, setEditingManualReception] = useState(null);

    // Wrappers that go back in history when a dialog was opened from the external drawer
    const wrapSetDetailDialog = (value) => {
        setDetailDialog(value);
        if (!value && openedFromDrawer) {
            setOpenedFromDrawer(false);
            navigate(-1);
        }
    };
    const wrapSetManualReceptionDetailDialog = (value) => {
        setManualReceptionDetailDialog(value);
        if (!value && openedFromDrawer) {
            setOpenedFromDrawer(false);
            navigate(-1);
        }
    };
    
    // Create order form
    const todayLocal = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const [orderForm, setOrderForm] = useState({
        supplier_id: '',
        site_id: '',
        notes: '',
        order_date: todayLocal(),
        items: []
    });
    const [currentItem, setCurrentItem] = useState({
        supplier_product_id: '',
        quantity: 1,
        order_mode: 'packaging' // 'packaging' = par colisage, 'detail' = au détail
    });

    // Reset the create-order form whenever the dialog closes (cancel OR validate),
    // so opening "Nouvelle commande" again starts from a clean slate. Otherwise
    // the previously selected product / quantity stays preselected.
    const wrapSetCreateDialog = (value) => {
        if (!value) {
            setOrderForm({ supplier_id: '', site_id: '', notes: '', order_date: todayLocal(), items: [] });
            setCurrentItem({ supplier_product_id: '', quantity: 1, order_mode: 'packaging' });
            setOrderableMode('normal');  // reset so the next order doesn't inherit "exceptional" mode
        }
        setCreateDialog(value);
    };

    useEffect(() => {
        loadData();
        suppliersAPI.getEmailTestMode().then(r => setEmailTestMode(r.data.enabled)).catch(() => {});
    }, []);

    // Handle mobile back button to close order detail dialog
    useEffect(() => {
        const handlePopState = () => {
            if (detailDialog) {
                setDetailDialog(false);
                setEditOrderMode(false);
                setEditOrderItems([]);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [detailDialog]);

    // Helper to get the order mode label based on product settings
    const getOrderModeLabel = (item, sp = null) => {
        if (!sp) {
            sp = supplierProducts.find(p => p.id === item.supplier_product_id);
        }
        const orderMode = item.order_mode || 'packaging';
        const hasUnknownPackaging = sp?.unknown_packaging || false;
        const hasPackaging = sp?.packaging_qty && !hasUnknownPackaging;
        
        if (orderMode === 'detail') {
            return { label: 'Détail', className: 'bg-amber-100 text-amber-800' };
        } else if (hasUnknownPackaging || !hasPackaging) {
            return { label: 'Unité', className: 'bg-slate-100 text-slate-700' };
        } else {
            return { label: 'Colisage', className: 'bg-blue-100 text-blue-800' };
        }
    };

    // Helper function to calculate line total with packaging
    // unit_price is expressed per invoice_unit.
    // - If invoice_unit matches order_unit (case-insensitive) => bill once per order unit (no × packaging_qty)
    // - Otherwise, unit_price is per billing_unit => × packaging_qty
    const calculateLineTotal = (item, sp = null) => {
        if (!sp) {
            sp = supplierProducts.find(p => p.id === item.supplier_product_id);
        }
        const packagingQty = sp?.packaging_qty;
        // Infer order_mode when missing (legacy data)
        let orderMode = item.order_mode;
        if (!orderMode) {
            const detailTotal = item.quantity * item.unit_price;
            const pkgTotal = item.quantity * (packagingQty || 1) * item.unit_price;
            if (item.total != null && Math.abs(item.total - detailTotal) < 0.02 && Math.abs(detailTotal - pkgTotal) > 0.02) {
                orderMode = 'detail';
            } else {
                orderMode = 'packaging';
            }
        }
        const orderUnit = (sp?.order_unit || '').toLowerCase();
        const billingUnitLow = (sp?.billing_unit || '').toLowerCase();
        const invoiceUnit = (sp?.invoice_unit || sp?.billing_unit || '').toLowerCase();

        // Mode détail : qty en billing_unit. Si invoice_unit ≠ billing_unit (ex: prix par Carton, qty en Poche), convertir.
        if (orderMode === 'detail') {
            if (packagingQty && billingUnitLow && invoiceUnit && billingUnitLow !== invoiceUnit) {
                return item.quantity * (item.unit_price / packagingQty);
            }
            return item.quantity * item.unit_price;
        }

        if (!packagingQty) {
            return item.quantity * item.unit_price;
        }

        // Mode colisage : si le prix est déjà au carton (invoice_unit == order_unit), pas de multiplication
        if (invoiceUnit && orderUnit && invoiceUnit === orderUnit) {
            return item.quantity * item.unit_price;
        }

        // Prix par pièce (billing_unit) : multiplier par le colisage
        return item.quantity * packagingQty * item.unit_price;
    };

    // Helper to get billing info for display
    const getBillingInfo = (item, sp = null) => {
        if (!sp) {
            sp = supplierProducts.find(p => p.id === item.supplier_product_id);
        }
        const orderUnit = sp?.order_unit || 'pièce';
        const billingUnit = sp?.billing_unit || 'pièce';
        const invoiceUnit = sp?.invoice_unit || billingUnit;
        const packagingQty = sp?.packaging_qty;
        // Infer order_mode when missing
        let orderMode = item.order_mode;
        if (!orderMode) {
            const detailTotal = item.quantity * item.unit_price;
            const pkgTotal = item.quantity * (packagingQty || 1) * item.unit_price;
            if (item.total != null && Math.abs(item.total - detailTotal) < 0.02 && Math.abs(detailTotal - pkgTotal) > 0.02) {
                orderMode = 'detail';
            } else {
                orderMode = 'packaging';
            }
        }
        
        // Mode détail : commande directe en unité de colisage
        if (orderMode === 'detail') {
            return {
                qtyOrdered: `${item.quantity} ${billingUnit}`,
                qtyBilled: `${item.quantity} ${invoiceUnit}`,
                priceLabel: `${formatCurrency(item.unit_price)}/${invoiceUnit}`,
                hasPackaging: false,
                orderMode: 'detail',
                billingUnit: billingUnit,
                invoiceUnit: invoiceUnit
            };
        }
        
        // Mode colisage : "Qté à facturer" = nombre de pièces livrées (info), mais priceLabel reste par invoice_unit
        if (packagingQty) {
            const qtyBilledDisplay = item.quantity * packagingQty;
            return {
                qtyOrdered: `${item.quantity} ${orderUnit}`,
                qtyBilled: `${qtyBilledDisplay} ${billingUnit}`,
                priceLabel: `${formatCurrency(item.unit_price)}/${invoiceUnit}`,
                hasPackaging: true,
                orderMode: 'packaging',
                billingUnit: billingUnit,
                invoiceUnit: invoiceUnit,
                packagingQty: packagingQty
            };
        }
        
        // Pas de colisage défini
        return {
            qtyOrdered: `${item.quantity} ${orderUnit}`,
            qtyBilled: `${item.quantity} ${invoiceUnit}`,
            priceLabel: `${formatCurrency(item.unit_price)}/${invoiceUnit}`,
            hasPackaging: false,
            orderMode: 'packaging',
            billingUnit: billingUnit,
            invoiceUnit: invoiceUnit
        };
    };

    const loadData = async () => {
        try {
            const [ordersRes, suppliersRes, sitesRes, spRes, ipRes, manualReceptionsRes, transfersRes] = await Promise.all([
                ordersAPI.getAll(),
                suppliersAPI.getAll().catch(() => ({ data: [] })),
                sitesAPI.getAll().catch(() => ({ data: [] })),
                supplierProductsAPI.getAll().catch(() => ({ data: [] })),
                industrialProductsAPI.getAll().catch(() => ({ data: [] })),
                ordersAPI.getManualReceptions ? ordersAPI.getManualReceptions().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                transfersAPI.getAll({ status: 'validated' }).catch(() => ({ data: [] }))
            ]);
            setOrders(ordersRes.data);
            setSuppliers(suppliersRes.data);
            // Appliquer le filtre des sites autorisés
            setSites(getAllowedSites(sitesRes.data));
            setSupplierProducts(spRes.data);
            setIndustrialProducts(ipRes.data);
            setManualReceptions(manualReceptionsRes.data || []);
            setValidatedTransfers(transfersRes.data || []);
            // Trash count (best-effort, silent on error)
            try {
                const tr = await api.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/trash`);
                setTrashCount(Array.isArray(tr.data) ? tr.data.length : 0);
            } catch (_) { /* ignore */ }

            // Auto-open reception or order from URL params (e.g., from drawer)
            const params = new URLSearchParams(window.location.search);
            const openOrderId = params.get('openOrder');
            const openReceptionId = params.get('openReception');
            if (openOrderId) {
                const o = ordersRes.data.find(x => x.id === openOrderId);
                if (o) {
                    setSelectedOrder(o);
                    setDetailDialog(true);
                    setOpenedFromDrawer(true);
                }
                window.history.replaceState({}, '', window.location.pathname);
            } else if (openReceptionId) {
                const mr = (manualReceptionsRes.data || []).find(x => x.id === openReceptionId);
                if (mr) {
                    setSelectedManualReception(mr);
                    setManualReceptionDetailDialog(true);
                    setOpenedFromDrawer(true);
                }
                window.history.replaceState({}, '', window.location.pathname);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    // Vérifier si un site est autorisé pour l'utilisateur
    const isSiteAllowed = (siteId) => {
        if (!user?.allowed_sites || user.allowed_sites.length === 0) {
            return true; // Accès à tous les sites
        }
        return user.allowed_sites.includes(siteId);
    };

    const isSiteExcluded = (siteId) =>
        excludedSites.includes(siteId) || excludedCompanies.some(c => isSiteInCompany(siteId, c));

    const toggleExcludeSite = (siteId) => {
        setExcludedSites(prev => prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]);
        if (filterSite === siteId) setFilterSite('all');
    };

    const toggleExcludeCompany = (name) => {
        setExcludedCompanies(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
        if (filterCompany === name) setFilterCompany('all');
    };

    const clearExclusions = () => { setExcludedSites([]); setExcludedCompanies([]); };

    const filteredOrders = orders.filter(order => {
        // Filtrer par site autorisé
        if (!isSiteAllowed(order.site_id)) return false;
        if (isSiteExcluded(order.site_id)) return false;
        const matchesSearch = (order.order_number || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        const matchesSupplier = filterSupplier === 'all' || order.supplier_id === filterSupplier;
        const matchesSite = filterSite === 'all' || order.site_id === filterSite;
        const matchesCompany = filterCompany === 'all' || isSiteInCompany(order.site_id, filterCompany);
        const matchesStatus = filterStatus === 'all'
            || (filterStatus === 'received' && ['delivered', 'partially_received'].includes(order.status))
            || (filterStatus === 'in_progress' && !['delivered', 'partially_received', 'cancelled'].includes(order.status));
        const matchesPeriod = searchTerm ? true : isInPeriod(order.created_at);
        return matchesSearch && matchesSupplier && matchesSite && matchesCompany && matchesStatus && matchesPeriod;
    });

    // Helper to determine if an order is a "Commande" (email sent) or "Réception manuelle" (no email)
    // Determine the type of an order based on its state
    // 'draft' = Created but not sent or received yet
    // 'ordered' = Email sent to supplier
    // 'reception' = Directly received without sending email
    const getOrderType = (order) => {
        if (order.email_sent_at) {
            return 'ordered'; // Mail envoyé = Commandé
        }
        // Check if order has any reception (status indicates reception happened)
        const hasReception = order.status === 'delivered' || 
                            order.status === 'partially_received' ||
                            order.received_at;
        if (hasReception) {
            return 'reception'; // Réceptionné sans mail = Réception
        }
        return 'draft'; // Ni envoyé ni réceptionné = Saisie
    };

    // Unified list combining orders and manual receptions
    const unifiedList = React.useMemo(() => [
        // Add orders with type marker based on state
        ...orders.map(order => ({
            ...order,
            _type: getOrderType(order),
            _number: order.order_number,
            _date: order.received_at || order.created_at,
            _sortDate: new Date(order.received_at || order.created_at).getTime(),
            _isFromOrders: true // To distinguish from the old manual_receptions collection
        })),
        // Add legacy manual receptions (from separate collection) with type marker
        ...manualReceptions.map(reception => ({
            ...reception,
            _type: 'reception',
            _number: reception.reception_number,
            _date: reception.reception_date || reception.created_at,
            _sortDate: new Date(reception.reception_date || reception.created_at).getTime(),
            status: 'delivered', // Manual receptions are always "delivered"
            _isFromOrders: false
        })),
        // Incoming transfers: one row per destination site, so the receiving
        // site sees what arrived (with source site + product suppliers)
        ...validatedTransfers.flatMap(t => (t.destinations || []).map(dest => {
            const items = dest.items || [];
            const supplierNames = [...new Set(items.map(it => it.supplier_name).filter(Boolean))];
            const supplierIds = [...new Set(items.map(it => supplierProducts.find(sp => sp.id === it.supplier_product_id)?.supplier_id).filter(Boolean))];
            const total = items.reduce((s, it) => s + (it.quantity || 0) * (it.price_per_unit || 0), 0);
            return {
                id: `${t.id}-${dest.site_id}`,
                _type: 'transfer_in',
                _number: t.transfer_number,
                _date: t.transfer_date || t.created_at,
                _sortDate: new Date(t.transfer_date || t.created_at).getTime(),
                _isFromOrders: false,
                _transfer: t,
                _dest: dest,
                _supplierIds: supplierIds,
                site_id: dest.site_id,
                site_name: dest.site_name,
                from_site_name: t.from_site_name,
                supplier_name: supplierNames.join(', ') || '—',
                total_amount: total,
                document_number: t.transfer_number,
                status: 'delivered',
            };
        }))
    ]
    .filter(item => {
        // Filter by allowed sites for user
        if (!isSiteAllowed(item.site_id)) return false;
        if (isSiteExcluded(item.site_id)) return false;
        // Filter by search
        const matchesSearch = (item._number || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        // Filter by supplier
        const matchesSupplier = filterSupplier === 'all' || item.supplier_id === filterSupplier || (item._supplierIds || []).includes(filterSupplier);
        // Filter by site
        const matchesSite = filterSite === 'all' || item.site_id === filterSite;
        const matchesCompany = filterCompany === 'all' || isSiteInCompany(item.site_id, filterCompany);
        // Filter by status (grouped: received = delivered/partial, in_progress = other non-cancelled)
        const matchesStatus = filterStatus === 'all'
            || (filterStatus === 'received' && ['delivered', 'partially_received'].includes(item.status))
            || (filterStatus === 'in_progress' && !['delivered', 'partially_received', 'cancelled'].includes(item.status));
        // Filter by type
        const matchesType = filterType === 'all' || item._type === filterType;
        // Filter by period: use pre-computed _date (received_at for orders, reception_date for manual)
        const matchesPeriod = searchTerm ? true : isInPeriod(item._date);
        return matchesSearch && matchesSupplier && matchesSite && matchesCompany && matchesStatus && matchesType && matchesPeriod;
    })
    .sort((a, b) => b._sortDate - a._sortDate), // Sort by date descending
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, manualReceptions, validatedTransfers, supplierProducts, user,
     searchTerm, filterSupplier, filterSite, filterCompany, filterStatus,
     filterType, filterPeriod, customPeriodStart, customPeriodEnd, excludedSites, excludedCompanies]);

    // Multi-company scoping: only for "Create Order" and "Manual Reception" flows.
    // Restrict the suppliers and industrial products shown to those matching the
    // user's active companies (derived from user.allowed_sites). Everywhere else
    // (view/edit existing orders, product & supplier lists, stats) stays unfiltered.
    const userCompanyFlags = getUserAllowedCompanyFlags(user, sites);
    const orderCreationSuppliers = filterByUserCompanies(suppliers, userCompanyFlags);
    const allowedIndustrialProductIds = React.useMemo(() => {
        const all = Object.values(COMPANY_FLAG_KEYS);
        if (!userCompanyFlags || userCompanyFlags.length >= all.length) return null; // no filter
        const set = new Set(userCompanyFlags);
        const ids = new Set();
        for (const ip of industrialProducts) {
            const hasAny = all.some(f => ip[f] === true);
            if (!hasAny || all.some(f => ip[f] === true && set.has(f))) ids.add(ip.id);
        }
        return ids;
    }, [industrialProducts, userCompanyFlags]);
    const isSupplierProductAllowedForUser = (sp) => {
        if (!allowedIndustrialProductIds) return true;
        return allowedIndustrialProductIds.has(sp.industrial_product_id);
    };

    // Filter and sort supplier products by category then alphabetically.
    // When `hideAddedProducts` is ON, exclude products already present in the order being created.
    // Privileged users (admin/supervisor) can switch to a "all" view to also
    // see normally-deactivated products. Other users always get the regular
    // commandable catalogue.
    const isPrivileged = ['admin', 'supervisor'].includes(user?.role);
    const effectiveOrderableMode = isPrivileged ? orderableMode : 'normal';
    const matchOrderableMode = (sp) => (
        effectiveOrderableMode === 'all'
            ? true  // include both commandable and non-commandable products
            : sp.commandable !== false
    );
    const availableSupplierProducts = supplierProducts
        .filter(sp => sp.supplier_id === orderForm.supplier_id && matchOrderableMode(sp))
        .filter(isSupplierProductAllowedForUser)
        .filter(sp => !hideAddedProducts || !(orderForm.items || []).some(it => it.supplier_product_id === sp.id))
        .sort((a, b) => {
            // Get category from industrial product
            const catA = industrialProducts.find(p => p.id === a.industrial_product_id)?.category || 'Sans catégorie';
            const catB = industrialProducts.find(p => p.id === b.industrial_product_id)?.category || 'Sans catégorie';
            if (catA !== catB) return catA.localeCompare(catB);
            // Then sort by product name
            const nameA = a.supplier_name_product || a.industrial_product_name || '';
            const nameB = b.supplier_name_product || b.industrial_product_name || '';
            return nameA.localeCompare(nameB);
        });
    
    // Helper to get category for a supplier product
    const getSupplierProductCategory = (sp) => {
        const product = industrialProducts.find(p => p.id === sp.industrial_product_id);
        return product?.category || 'Sans catégorie';
    };

    const handleAddItem = () => {
        if (!currentItem.supplier_product_id || currentItem.quantity < 1) {
            toast.error('Sélectionnez un produit et une quantité valide');
            return;
        }
        
        const sp = supplierProducts.find(p => p.id === currentItem.supplier_product_id);
        if (!sp) return;
        
        const newItem = {
            supplier_product_id: sp.id,
            industrial_product_id: sp.industrial_product_id,
            quantity: currentItem.quantity,
            unit_price: sp.unit_price,
            product_name: sp.supplier_name_product || sp.industrial_product_name,
            supplier_ref: sp.supplier_ref,
            order_mode: currentItem.order_mode || 'packaging',
            // Store unit info for display and calculations
            order_unit: sp.order_unit || 'pièce',
            billing_unit: sp.billing_unit || 'pièce',
            invoice_unit: sp.invoice_unit || sp.billing_unit || 'pièce',
            packaging_qty: sp.packaging_qty
        };
        
        // Use functional update to avoid stale state issues
        setOrderForm(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
        setCurrentItem({ supplier_product_id: '', quantity: 1, order_mode: 'packaging' });
    };

    const handleRemoveItem = (index) => {
        setOrderForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleCreateOrder = async () => {
        if (!orderForm.supplier_id || !orderForm.site_id || orderForm.items.length === 0) {
            toast.error('Fournisseur, site et au moins un produit sont requis');
            return;
        }
        
        try {
            await ordersAPI.create({
                supplier_id: orderForm.supplier_id,
                site_id: orderForm.site_id,
                notes: orderForm.notes,
                order_date: orderForm.order_date || undefined,
                items: orderForm.items.map(item => ({
                    supplier_product_id: item.supplier_product_id,
                    industrial_product_id: item.industrial_product_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    order_mode: item.order_mode || 'packaging'
                }))
            });
            toast.success('Commande créée');
            wrapSetCreateDialog(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await ordersAPI.updateStatus(orderId, newStatus);
            toast.success('Statut mis à jour');
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Edit order functions
    const handleStartEditOrder = async () => {
        if (!selectedOrder) return;
        // Refetch the order before building the edit form: saving from a stale
        // items list silently deletes reception-added lines/notes (bug CMD-00398).
        let order = selectedOrder;
        try {
            const fresh = await ordersAPI.getOrderById(selectedOrder.id);
            if (fresh?.data) {
                order = fresh.data;
                setSelectedOrder(order);
            }
        } catch { /* fallback on current state */ }
        setEditOrderItems(order.items.map(item => ({
            ...item,
            supplier_product_id: item.supplier_product_id,
            quantity: item.quantity,
            unit_price: item.unit_price
        })));
        setEditOrderNotes(order.notes || '');
        // Only initialize the "date" field if the order already has a reception
        // date. Falling back to `created_at` for pure drafts would set
        // `received_at` on save → the order would then be classified as
        // "réception manuelle" and the edit dialog's Add-product UI would
        // switch to the reception flow (regression seen on CMD-00294).
        const existingDate = order.received_at || '';
        setEditOrderDate(existingDate ? String(existingDate).slice(0, 10) : '');
        // Pick the first document_number found in reception history (or on the order itself)
        const firstDoc = order.document_number
            || (receptionHistory.find(r => r.document_number)?.document_number)
            || order.invoice_number
            || '';
        setEditOrderDocNumber(firstDoc);
        setAddItemToOrder({ supplier_product_id: '', quantity: 1, order_mode: 'packaging' });
        setEditOrderMode(true);
    };

    const handleCancelEditOrder = () => {
        setEditOrderMode(false);
        setEditOrderItems([]);
        setEditOrderNotes('');
    };

    const handleAddItemToEditOrder = () => {
        if (!addItemToOrder.supplier_product_id || addItemToOrder.quantity < 1) {
            toast.error('Sélectionnez un produit et une quantité valide');
            return;
        }
        
        const sp = supplierProducts.find(p => p.id === addItemToOrder.supplier_product_id);
        if (!sp) return;
        
        // Check if product already exists with same order_mode
        const existingIndex = editOrderItems.findIndex(item => 
            item.supplier_product_id === addItemToOrder.supplier_product_id && 
            item.order_mode === addItemToOrder.order_mode
        );
        if (existingIndex >= 0) {
            // Update quantity
            const newItems = [...editOrderItems];
            newItems[existingIndex].quantity += addItemToOrder.quantity;
            setEditOrderItems(newItems);
        } else {
            // Add new item with order_mode
            setEditOrderItems([...editOrderItems, {
                supplier_product_id: sp.id,
                industrial_product_id: sp.industrial_product_id,
                supplier_ref: sp.supplier_ref,
                product_name: sp.supplier_name_product || sp.industrial_product_name,
                unit_price: sp.unit_price,
                quantity: addItemToOrder.quantity,
                total: sp.unit_price * addItemToOrder.quantity,
                order_mode: addItemToOrder.order_mode,
                order_unit: sp.order_unit || 'pièce',
                billing_unit: sp.billing_unit || 'pièce',
                invoice_unit: sp.invoice_unit || sp.billing_unit || 'pièce',
                packaging_qty: sp.packaging_qty || 1
            }]);
        }
        
        setAddItemToOrder({ supplier_product_id: '', quantity: 1, unit_price: 0, order_mode: 'packaging' });
    };

    // Check if order is in "reception" mode (has receptions but no email sent)
    const isOrderInReceptionMode = (order) => {
        if (!order) return false;
        const hasReception = order.status === 'delivered' || 
                            order.status === 'partially_received' ||
                            order.received_at;
        return !order.email_sent_at && hasReception;
    };

    // Add a new reception item directly (for orders already in reception mode)
    const handleAddReceptionItemDirect = async () => {
        if (!addReceptionItem.supplier_product_id || !(parseFloat(addReceptionItem.quantity) > 0)) {
            toast.error('Sélectionnez un produit et une quantité valide');
            return;
        }
        
        const sp = supplierProducts.find(p => p.id === addReceptionItem.supplier_product_id);
        if (!sp) return;
        
        try {
            // Create a reception directly via the receive API.
            // The user types the quantity in the BILLING unit (kg, pièce…):
            // send is_detail_mode so the backend does NOT multiply by packaging_qty
            // (bug CMD-00398 : 17,27 kg saisi → 276,32 enregistré).
            await ordersAPI.receive(selectedOrder.id, {
                items: [{
                    supplier_product_id: sp.id,
                    quantity_received: addReceptionItem.quantity,
                    unit_price: addReceptionItem.unit_price || sp.unit_price,
                    is_detail_mode: true,
                    actual_weight: sp.variable_weight ? addReceptionItem.quantity : null
                }],
                document_number: null,
                reception_date: selectedOrder.received_at || new Date().toISOString().split('T')[0]
            });
            
            toast.success('Produit réceptionné ajouté');
            
            // Refresh data
            await loadReceptionHistory(selectedOrder.id);
            loadData();
            
            // Refresh selected order
            const response = await ordersAPI.getAll();
            const updatedOrder = response.data.find(o => o.id === selectedOrder.id);
            if (updatedOrder) setSelectedOrder(updatedOrder);
            
            // Reset form
            setAddReceptionItem({ 
                supplier_product_id: '', 
                quantity: 1, 
                unit_price: 0,
                date: new Date().toISOString().split('T')[0],
                document_number: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
        }
    };

    const handleRemoveItemFromEditOrder = (index) => {
        setEditOrderItems(editOrderItems.filter((_, i) => i !== index));
    };

    const handleUpdateOrderItemQuantity = (index, newQuantity) => {
        const newItems = [...editOrderItems];
        const item = newItems[index];
        const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
        const isVariableWeight = !!sp?.variable_weight && (item.order_mode || 'packaging') !== 'packaging';
        const parsed = isVariableWeight
            ? (parseFloat(newQuantity) || 0)
            : (parseInt(newQuantity) || 0);
        item.quantity = parsed;
        item.total = calculateLineTotal(item);
        setEditOrderItems(newItems);
    };

    const handleUpdateOrderItemReceived = (index, newValue) => {
        const newItems = [...editOrderItems];
        newItems[index] = { ...newItems[index], quantity_received: newValue === '' ? '' : (parseFloat(newValue) || 0), received_edited: true };
        setEditOrderItems(newItems);
    };

    const handleUpdateOrderItemMode = (index, newMode) => {
        const newItems = [...editOrderItems];
        const item = newItems[index];
        const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
        const pkg = sp?.packaging_qty || 1;
        const previousMode = item.order_mode || 'packaging';
        if (previousMode !== newMode && pkg > 1) {
            if (newMode === 'detail' && previousMode === 'packaging') {
                // Colisage -> Unité : multiplier par pkg
                item.quantity = (item.quantity || 0) * pkg;
            } else if (newMode === 'packaging' && previousMode === 'detail') {
                // Unité -> Colisage : diviser par pkg
                item.quantity = (item.quantity || 0) / pkg;
            }
        }
        item.order_mode = newMode;
        item.total = calculateLineTotal(item);
        setEditOrderItems(newItems);
    };

    const handleSaveOrderChanges = async () => {
        if (editOrderItems.length === 0) {
            toast.error('La commande doit contenir au moins un produit');
            return;
        }
        
        try {
            await ordersAPI.update(selectedOrder.id, {
                items: editOrderItems.map(item => ({
                    supplier_product_id: item.supplier_product_id,
                    industrial_product_id: item.industrial_product_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    order_mode: item.order_mode || 'packaging',
                    quantity_received: item.quantity_received === '' || item.quantity_received == null
                        ? null
                        : parseFloat(item.quantity_received),
                    // Explicitly setting received qty to 0 closes the line (won't reappear
                    // in reception); setting it > 0 reopens a previously closed line.
                    closed: item.received_edited && item.quantity_received !== '' && item.quantity_received != null
                        ? parseFloat(item.quantity_received) === 0
                        : null,
                })),
                notes: editOrderNotes,
                received_at: editOrderDate || null,
                document_number: editOrderDocNumber || null,
            });
            toast.success('Commande modifiée');
            setEditOrderMode(false);
            loadData();
            // Refresh selected order
            const response = await ordersAPI.getAll();
            const updatedOrder = response.data.find(o => o.id === selectedOrder.id);
            if (updatedOrder) setSelectedOrder(updatedOrder);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const handleDeleteClick = (order) => {
        setOrderToDelete(order);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const res = await ordersAPI.delete(orderToDelete.id);
            // Backend now returns a soft-delete message — surface that to the user
            const msg = res?.data?.message || 'Commande supprimée';
            toast.success(`${msg} — restaurable 30 jours`, { duration: 5000 });
            setDeleteDialog(false);
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleExport = () => {
        const params = {};
        if (filterSupplier !== 'all') params.supplier_id = filterSupplier;
        if (filterSite !== 'all') params.site_id = filterSite;
        downloadAuthFile(exportAPI.orders(params), 'commandes.xlsx');
    };

    const handleSendEmail = async (order) => {
        try {
            const response = await ordersAPI.sendEmail(order.id);
            toast.success(response.data.message);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
        }
    };

    // Cloture manuelle des quantites restantes (rattrapage apres oubli)
    const [closeRemainingConfirmOrder, setCloseRemainingConfirmOrder] = useState(null);
    const handleCloseRemainingQuantities = async (orderId) => {
        try {
            await ordersAPI.closeRemaining(orderId);
            toast.success('Quantités restantes clôturées');
            loadData();
            // Rafraichir selectedOrder si c'est celui-la
            if (selectedOrder?.id === orderId) {
                const response = await ordersAPI.getAll();
                const updated = response.data.find(o => o.id === orderId);
                if (updated) setSelectedOrder(updated);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
        } finally {
            setCloseRemainingConfirmOrder(null);
        }
    };

    // Reception handlers
    const handleOpenReception = async (order) => {
        // Guard against accidentally receiving a non-sent draft order
        // (the "Réceptionner" icon sits right next to "Modifier" and can be confused)
        const isDraft = !order.email_sent_at && !order.received_at
            && order.status !== 'delivered'
            && order.status !== 'partially_received';
        if (isDraft) {
            const ok = window.confirm(
                `Cette commande ${order.order_number} n'a pas encore été envoyée au fournisseur ` +
                `et n'a aucune réception.\n\n` +
                `Passer en mode réception ?\n\n` +
                `• OK : ouvrir la fenêtre de réception (statut → "partiellement reçue")\n` +
                `• Annuler : revenir à la liste (utilisez "Modifier" pour ajouter des produits avant envoi)`
            );
            if (!ok) return;
        }
        setSelectedOrder(order);
        // Build the fresh items list from the order (closed lines are excluded:
        // they were explicitly marked as not-to-be-received — bug CMD-00398)
        const items = order.items.filter(item => !item.closed).map(item => {
            const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
            const orderUnit = sp?.order_unit || 'pièce';
            const billingUnit = sp?.billing_unit || 'pièce';
            const invoiceUnit = sp?.invoice_unit || billingUnit;
            const packagingQty = sp?.packaging_qty || 1;
            const orderMode = item.order_mode || 'packaging';
            const isDetailMode = orderMode === 'detail';
            const variableWeight = sp?.variable_weight || false;
            const sellByDetail = sp?.sell_by_detail || false;
            const unknownPackaging = sp?.unknown_packaging || false;

            let expectedBilledQty = item.quantity;
            if (!isDetailMode && packagingQty > 1 && !unknownPackaging) {
                expectedBilledQty = item.quantity * packagingQty;
            }

            const theoreticalWeight = !isDetailMode && packagingQty > 1 && !unknownPackaging
                ? (item.quantity - (item.quantity_received || 0)) * packagingQty
                : item.quantity - (item.quantity_received || 0);

            const remainingOrdered = item.quantity - (item.quantity_received || 0);
            const remainingBilled = !isDetailMode && packagingQty > 1 && !unknownPackaging
                ? remainingOrdered * packagingQty
                : remainingOrdered;

            return {
                supplier_product_id: item.supplier_product_id,
                product_name: item.supplier_product_name || 'Produit',
                quantity_ordered: item.quantity,
                quantity_received: item.quantity_received || 0,
                quantity_to_receive: remainingOrdered,
                quantity_to_receive_detail: remainingBilled,
                unit_price: item.unit_price,
                original_unit_price: item.unit_price,
                order_mode: orderMode,
                order_unit: orderUnit,
                billing_unit: billingUnit,
                invoice_unit: invoiceUnit,
                packaging_qty: packagingQty,
                expected_billed_qty: expectedBilledQty,
                is_detail_mode: isDetailMode,
                variable_weight: variableWeight,
                actual_weight: variableWeight ? theoreticalWeight : null,
                theoretical_weight: theoreticalWeight,
                sell_by_detail: sellByDetail,
                receive_by_detail: false,
                unknown_packaging: unknownPackaging,
            };
        });

        // Check for a saved draft and ask the user whether to resume it
        let draft = null;
        try {
            const resp = await ordersAPI.getReceptionDraft(order.id);
            draft = resp.data;
        } catch (err) {
            if (err.response?.status !== 404) {
                console.warn('Erreur chargement brouillon:', err);
            }
        }

        let finalItems = items;
        let finalDoc = '';
        let finalDate = new Date().toISOString().split('T')[0];
        let finalCloseRem = false;

        if (draft && Array.isArray(draft.items) && draft.items.length > 0) {
            const resume = window.confirm(
                `Une réception en cours a été enregistrée pour ${order.order_number}.\nVoulez-vous la reprendre ?\n\n[OK] Reprendre — [Annuler] Repartir de zéro`
            );
            if (resume) {
                // Merge draft into items by supplier_product_id; extras come last
                const byId = new Map();
                items.forEach(it => byId.set(it.supplier_product_id, it));
                const merged = [];
                draft.items.forEach(dItem => {
                    const base = byId.get(dItem.supplier_product_id);
                    if (base) {
                        merged.push({ ...base, ...dItem });
                        byId.delete(dItem.supplier_product_id);
                    } else {
                        // Extra item (not in order) — keep as-is
                        merged.push({ ...dItem, is_extra: true });
                    }
                });
                // Append remaining order lines not present in draft
                byId.forEach(it => merged.push(it));
                finalItems = merged;
                finalDoc = draft.document_number || '';
                finalDate = draft.reception_date || finalDate;
                finalCloseRem = !!draft.close_remaining;
                toast.info('Brouillon repris');
            } else {
                // Discard the draft on the server
                try { await ordersAPI.deleteReceptionDraft(order.id); } catch { /* ignore */ }
            }
        }

        setReceptionItems(finalItems);
        setCloseRemainingQuantities(finalCloseRem);
        setDocumentNumber(finalDoc);
        setReceptionDate(finalDate);
        setExtraReceptionItem({ supplier_product_id: '', quantity: '', unit_price: '' });
        setReceptionDialog(true);
    };

    const handleSaveReceptionDraft = async () => {
        if (!selectedOrder) return;
        try {
            // Serialize only the fields we need to rebuild the dialog state
            const payload = {
                document_number: documentNumber.trim() || '',
                reception_date: receptionDate,
                close_remaining: closeRemainingQuantities,
                items: receptionItems.map(it => ({
                    supplier_product_id: it.supplier_product_id,
                    product_name: it.product_name,
                    quantity_ordered: it.quantity_ordered,
                    quantity_received: it.quantity_received,
                    quantity_to_receive: it.quantity_to_receive,
                    quantity_to_receive_detail: it.quantity_to_receive_detail,
                    unit_price: it.unit_price,
                    original_unit_price: it.original_unit_price,
                    order_mode: it.order_mode,
                    order_unit: it.order_unit,
                    billing_unit: it.billing_unit,
                    invoice_unit: it.invoice_unit,
                    packaging_qty: it.packaging_qty,
                    expected_billed_qty: it.expected_billed_qty,
                    is_detail_mode: it.is_detail_mode,
                    variable_weight: it.variable_weight,
                    actual_weight: it.actual_weight,
                    theoretical_weight: it.theoretical_weight,
                    sell_by_detail: it.sell_by_detail,
                    receive_by_detail: it.receive_by_detail,
                    unknown_packaging: it.unknown_packaging,
                    is_extra: it.is_extra || false,
                    replaces_supplier_product_id: it.replaces_supplier_product_id || null,
                    replaces_product_name: it.replaces_product_name || null,
                    replaced: it.replaced || false,
                    replaced_by_supplier_product_id: it.replaced_by_supplier_product_id || null,
                    override_supplier_price: it.override_supplier_price || false,
                })),
            };
            await ordersAPI.saveReceptionDraft(selectedOrder.id, payload);
            toast.success('Brouillon enregistré — vous pourrez reprendre plus tard');
            setReceptionDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du brouillon');
        }
    };

    const [pendingReceptionSave, setPendingReceptionSave] = useState(null); // { itemsToReceive, incompleteLines }

    // Determine if the reception would leave open (uncompleted) lines.
    // A line is "incomplete" when its remaining_qty (before this reception) is > 0
    // AND the quantity we're about to receive is less than what remains.
    const detectIncompleteReceptionLines = () => {
        const incomplete = [];
        receptionItems.forEach(item => {
            if (item.is_extra) return; // extras are new lines, not "unclosed" ordered lines
            if (item.replaced) return; // replaced lines are already being closed via rupture flow

            const orderedQty = item.quantity_ordered || 0;
            const alreadyReceived = item.quantity_received || 0;
            const toReceive = item.receive_by_detail
                ? (item.quantity_to_receive_detail || 0) / (item.packaging_qty || 1)
                : (item.quantity_to_receive || 0);
            const remainingAfter = orderedQty - alreadyReceived - toReceive;
            if (remainingAfter > 0.0001) {
                incomplete.push({
                    name: item.product_name || item.supplier_product_name || 'Produit',
                    remaining: remainingAfter,
                    unit: item.order_unit || item.billing_unit || 'u.'
                });
            }
        });
        return incomplete;
    };

    const buildItemsToReceivePayload = () => (
        receptionItems
            .filter(item => {
                if (item.replaced) return true;
                if (item.variable_weight) {
                    return parseFloat(item.actual_weight) > 0 || item.unit_price !== item.original_unit_price;
                }
                if (item.receive_by_detail && item.quantity_to_receive_detail > 0) return true;
                if (item.quantity_to_receive > 0) return true;
                if (item.unit_price !== item.original_unit_price) return true;
                return false;
            })
            .map(item => {
                let quantityReceived = item.quantity_to_receive;
                if (item.receive_by_detail && !item.is_detail_mode) {
                    quantityReceived = item.quantity_to_receive_detail;
                }
                if (item.variable_weight && !(parseFloat(item.actual_weight) > 0)) {
                    quantityReceived = 0;
                }
                return {
                    supplier_product_id: item.supplier_product_id,
                    quantity_received: quantityReceived,
                    unit_price: item.unit_price,
                    actual_weight: item.variable_weight
                        ? (item.actual_weight === '' || item.actual_weight == null ? 0 : parseFloat(item.actual_weight) || 0)
                        : null,
                    receive_by_detail: item.receive_by_detail || false,
                    override_supplier_price: item.override_supplier_price || false,
                    replaced_by_supplier_product_id: item.replaced_by_supplier_product_id || null,
                    is_extra: item.is_extra || false,
                    is_detail_mode: item.is_extra ? !!item.is_detail_mode : undefined,
                };
            })
    );

    // Effectue l'appel API de la reception avec les items et le flag close_remaining
    const performReceptionSave = async (itemsToReceive, closeFlag) => {
        if (receptionSaving) return; // anti double-clic
        setReceptionSaving(true);
        try {
            const response = await ordersAPI.receive(selectedOrder.id, {
                items: itemsToReceive,
                close_remaining: closeFlag,
                document_number: documentNumber.trim() || null,
                reception_date: receptionDate
            });
            toast.success(response.data.message);
            setReceptionDialog(false);
            setDocumentNumber('');
            setReceptionDate(new Date().toISOString().split('T')[0]);
            setPendingReceptionSave(null);
            loadData();
            // Refresh selectedOrder: the detail dialog may still be open behind
            // and a later "Modifier" from stale items would erase this reception.
            try {
                const fresh = await ordersAPI.getOrderById(selectedOrder.id);
                if (fresh?.data) setSelectedOrder(fresh.data);
            } catch { /* non bloquant */ }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la réception');
        } finally {
            setReceptionSaving(false);
        }
    };

    const handleSaveReception = async () => {
        const itemsToReceive = buildItemsToReceivePayload();

        if (itemsToReceive.length === 0 && !closeRemainingQuantities) {
            toast.error('Aucune quantité à réceptionner');
            return;
        }

        // Garde-fou : lignes non entierement recues + case "cloturer" non cochee
        if (!closeRemainingQuantities) {
            const incomplete = detectIncompleteReceptionLines();
            if (incomplete.length > 0) {
                setPendingReceptionSave({ itemsToReceive, incompleteLines: incomplete });
                return;
            }
        }

        await performReceptionSave(itemsToReceive, closeRemainingQuantities);
    };

    // Add an extra product (not in original order) to the in-progress reception.
    // We allow duplicates: same supplier_product_id can appear multiple times
    // so the user can record the same SKU at different prices in a single reception
    // (e.g. partial discount). Each duplicate is flagged is_extra=true so the backend
    // appends it as a NEW line instead of merging it into the original ordered line.
    const handleAddExtraReceptionItem = () => {
        if (!extraReceptionItem.supplier_product_id) {
            toast.error('Sélectionnez un produit');
            return;
        }
        const qty = parseFloat(extraReceptionItem.quantity);
        if (!qty || qty <= 0) {
            toast.error('Quantité invalide');
            return;
        }
        const sp = supplierProducts.find(p => p.id === extraReceptionItem.supplier_product_id);
        if (!sp) {
            toast.error('Produit introuvable');
            return;
        }
        const packagingQty = sp.packaging_qty || 1;
        const orderUnit = (sp.order_unit || '').trim() || 'u.';
        const invoiceUnit = (sp.invoice_unit || sp.billing_unit || '').trim() || orderUnit;
        // Pour un ajout supplement, l'utilisateur saisit qty ET prix DIRECTEMENT
        // dans l'unite de facturation — on aligne billing_unit sur invoice_unit
        // pour eviter que la logique "detail-mixed-units" ne divise le prix
        // par le colisage.
        const billingUnit = invoiceUnit;
        // On force le mode "detail" pour cette ligne (pas de multiplication par colisage).
        const isDetailMode = true;
        const variableWeight = !!sp.variable_weight;
        const unitPrice = parseFloat(extraReceptionItem.unit_price) || sp.unit_price || 0;

        const expectedBilledQty = qty;

        const newLine = {
            supplier_product_id: sp.id,
            product_name: sp.supplier_name_product || sp.industrial_product_name || 'Produit',
            quantity_ordered: 0,
            quantity_received: 0,
            quantity_to_receive: qty,
            quantity_to_receive_detail: expectedBilledQty,
            unit_price: unitPrice,
            original_unit_price: unitPrice,
            order_mode: isDetailMode ? 'detail' : 'packaging',
            order_unit: orderUnit,
            billing_unit: billingUnit,
            invoice_unit: invoiceUnit,
            packaging_qty: packagingQty,
            expected_billed_qty: expectedBilledQty,
            is_detail_mode: isDetailMode,
            variable_weight: variableWeight,
            actual_weight: variableWeight ? expectedBilledQty : null,
            theoretical_weight: variableWeight ? expectedBilledQty : null,
            sell_by_detail: !!sp.sell_by_detail,
            receive_by_detail: false,
            unknown_packaging: false,
            is_extra: true,  // UI flag
        };
        setReceptionItems(prev => [...prev, newLine]);
        setExtraReceptionItem({ supplier_product_id: '', quantity: '', unit_price: '' });
        toast.success('Produit ajouté à la liste — pensez à valider la réception');
    };

    // ─── Supplier replacement (rupture) flow ──────────────────────────────────
    // When the supplier delivers a different product than the one ordered, the
    // user opens this dialog to pick a replacement supplier_product (same
    // supplier), set qty + price, and confirm. The original line is marked
    // `replaced` (greyed out, qty_to_receive = 0) while a new is_extra line is
    // added for the replacement. At save time, the backend closes the original
    // ordered line and creates the stock movement on the replacement.

    const handleOpenReplacement = (originalIndex) => {
        setReplacementDialog({ open: true, originalIndex });
        setReplacementForm({ supplier_product_id: '', quantity: '', unit_price: '' });
    };

    const handleConfirmReplacement = () => {
        const { originalIndex } = replacementDialog;
        if (originalIndex == null) return;
        if (!replacementForm.supplier_product_id) {
            toast.error('Sélectionnez un produit de remplacement');
            return;
        }
        const qty = parseFloat(replacementForm.quantity);
        if (!qty || qty <= 0) {
            toast.error('Quantité invalide');
            return;
        }
        if (receptionItems.some(it => it.supplier_product_id === replacementForm.supplier_product_id)) {
            toast.error('Ce produit est déjà dans la réception');
            return;
        }
        const sp = supplierProducts.find(p => p.id === replacementForm.supplier_product_id);
        if (!sp) {
            toast.error('Produit introuvable');
            return;
        }
        const original = receptionItems[originalIndex];
        const packagingQty = sp.packaging_qty || 1;
        const orderUnit = (sp.order_unit || '').trim() || 'u.';
        const invoiceUnit = (sp.invoice_unit || sp.billing_unit || '').trim() || orderUnit;
        // Pour un remplacement, l'utilisateur saisit qty ET prix DIRECTEMENT
        // dans l'unite de facturation — on aligne billing_unit sur invoice_unit
        // pour eviter la logique "detail-mixed-units" qui diviserait le prix
        // par le colisage.
        const billingUnit = invoiceUnit;
        // On force le mode "detail" pour cette ligne (pas de multiplication par colisage).
        const isDetailMode = true;
        const variableWeight = !!sp.variable_weight;
        const unitPrice = parseFloat(replacementForm.unit_price) || sp.unit_price || 0;
        const expectedBilledQty = qty;

        // 1) Mark the original line as "replaced": grey out, no quantity to receive
        // and remember which product replaced it (used by backend to close the line).
        const newItems = [...receptionItems];
        newItems[originalIndex] = {
            ...original,
            replaced: true,                                   // UI flag
            replaced_by_supplier_product_id: sp.id,           // sent to backend
            quantity_to_receive: 0,
            quantity_to_receive_detail: 0,
            actual_weight: original.variable_weight ? 0 : original.actual_weight,
        };

        // 2) Insert the replacement line RIGHT BELOW the replaced product
        //    (instead of appending at the end) so the user immediately sees the link
        //    between the original out-of-stock product and its substitute.
        const replacementLine = {
            supplier_product_id: sp.id,
            product_name: sp.supplier_name_product || sp.industrial_product_name || 'Produit',
            quantity_ordered: 0,
            quantity_received: 0,
            quantity_to_receive: qty,
            quantity_to_receive_detail: expectedBilledQty,
            unit_price: unitPrice,
            original_unit_price: unitPrice,
            order_mode: isDetailMode ? 'detail' : 'packaging',
            order_unit: orderUnit,
            billing_unit: billingUnit,
            invoice_unit: invoiceUnit,
            packaging_qty: packagingQty,
            expected_billed_qty: expectedBilledQty,
            is_detail_mode: isDetailMode,
            variable_weight: variableWeight,
            actual_weight: variableWeight ? expectedBilledQty : null,
            theoretical_weight: variableWeight ? expectedBilledQty : null,
            sell_by_detail: !!sp.sell_by_detail,
            receive_by_detail: false,
            unknown_packaging: false,
            is_extra: true,
            replaces_supplier_product_id: original.supplier_product_id,   // UI trace
            replaces_product_name: original.product_name,
        };
        newItems.splice(originalIndex + 1, 0, replacementLine);
        setReceptionItems(newItems);
        setReplacementDialog({ open: false, originalIndex: null });
        setReplacementForm({ supplier_product_id: '', quantity: '', unit_price: '' });
        toast.success(`Remplacement enregistré — ${original.product_name} → ${sp.supplier_name_product || sp.industrial_product_name}`);
    };

    // Supplier products that can be added as extras: same supplier as the order
    // AND (when in "normal" mode) commandable. Admin/supervisor can switch
    // the picker to "Tous" via the receptionOrderableMode toggle to log
    // unforeseen products that were delivered (incl. deactivated SKUs).
    // When `hideAddedProducts` is ON, exclude products already in receptionItems.
    // (Default OFF preserves the ability to add the same SKU twice at different prices.)
    const [receptionOrderableMode, setReceptionOrderableMode] = useState('normal');
    const effectiveReceptionMode = isPrivileged ? receptionOrderableMode : 'normal';
    const availableExtraReceptionProducts = supplierProducts.filter(sp => {
        if (!selectedOrder) return false;
        if (sp.supplier_id !== selectedOrder.supplier_id) return false;
        if (effectiveReceptionMode !== 'all' && sp.commandable === false) return false;
        if (hideAddedProducts && receptionItems.some(it => it.supplier_product_id === sp.id)) return false;
        return true;
    });

    // Check if there are any non-received quantities (before or after current reception)
    const hasRemainingQuantities = () => {
        return receptionItems.some(item => {
            // Check if there are quantities not yet received (regardless of what we're about to receive)
            const totalAfterReception = (item.quantity_received || 0) + (item.quantity_to_receive || 0);
            return totalAfterReception < item.quantity_ordered;
        });
    };

    // Check if there are any quantities that haven't been fully received yet
    const hasUnreceivedQuantities = () => {
        return receptionItems.some(item => {
            return (item.quantity_received || 0) < item.quantity_ordered;
        });
    };

    const calculateTotal = () => {
        return orderForm.items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
    };

    // Order Reception History handlers
    const loadReceptionHistory = async (orderId) => {
        try {
            const response = await ordersAPI.getReceptionHistory(orderId);
            setReceptionHistory(response.data);
        } catch (error) {
            console.error('Error loading reception history:', error);
            setReceptionHistory([]);
        }
    };

    const handleOpenOrderDetail = async (order) => {
        setSelectedOrder(order);
        setDetailDialog(true);
        // Always refresh from the server: the list row can be stale (e.g. right
        // after a reception done elsewhere) and editing from stale items would
        // silently erase reception changes (bug CMD-00398).
        try {
            const fresh = await ordersAPI.getOrderById(order.id);
            if (fresh?.data) setSelectedOrder(fresh.data);
        } catch { /* keep the list version */ }
        await loadReceptionHistory(order.id);
        // Push history state for mobile back button
        window.history.pushState({ orderDetail: true }, '');
    };

    const handleEditReception = (reception) => {
        setEditingReception({
            ...reception,
            date: reception.date || new Date().toISOString().split('T')[0]
        });
        setEditReceptionDialog(true);
    };

    const handleSaveReceptionEdit = async () => {
        if (!editingReception || !selectedOrder) return;
        
        try {
            await ordersAPI.updateReception(selectedOrder.id, [{
                movement_id: editingReception.id,
                quantity: editingReception.quantity,
                unit_price: editingReception.unit_price,
                date: editingReception.date,
                document_number: editingReception.document_number || null,
                override_supplier_price: !!editingReception.override_supplier_price,
            }]);
            toast.success('Réception modifiée');
            setEditReceptionDialog(false);
            setEditingReception(null);
            await loadReceptionHistory(selectedOrder.id);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const handleDeleteReception = async (reception) => {
        if (!selectedOrder) return;
        
        if (!confirm(`Supprimer cette réception de ${reception.quantity} ${reception.product_name} ?`)) {
            return;
        }
        
        try {
            await ordersAPI.deleteReception(selectedOrder.id, reception.id);
            toast.success('Réception supprimée');
            await loadReceptionHistory(selectedOrder.id);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    // Manual Reception — admin/supervisor can switch to "Tous" to see
    // non-commandable products too (e.g. a one-off purchase of a discontinued
    // item). Regular users always see the commandable catalogue.
    const [manualReceptionOrderableMode, setManualReceptionOrderableMode] = useState('normal');
    const effectiveManualMode = isPrivileged ? manualReceptionOrderableMode : 'normal';
    const availableManualReceptionProducts = supplierProducts.filter(sp => {
        if (sp.supplier_id !== manualReceptionForm.supplier_id) return false;
        if (effectiveManualMode !== 'all' && sp.commandable === false) return false;
        if (!isSupplierProductAllowedForUser(sp)) return false;
        if (hideAddedProducts && (manualReceptionForm.items || []).some(it => it.supplier_product_id === sp.id)) return false;
        return true;
    });

    const handleAddManualReceptionItem = () => {
        if (!manualReceptionItem.supplier_product_id || manualReceptionItem.quantity <= 0) {
            toast.error('Sélectionnez un produit et une quantité valide');
            return;
        }
        
        const sp = supplierProducts.find(p => p.id === manualReceptionItem.supplier_product_id);
        if (!sp) return;
        
        const unitPrice = manualReceptionItem.unit_price ? parseFloat(manualReceptionItem.unit_price) : sp.unit_price;
        
        const newItem = {
            supplier_product_id: sp.id,
            industrial_product_id: sp.industrial_product_id,
            quantity: manualReceptionItem.quantity,
            unit_price: unitPrice,
            product_name: sp.supplier_name_product || '',
            supplier_ref: sp.supplier_ref || '',
            industrial_product_name: sp.industrial_product_name || ''
        };
        
        setManualReceptionForm(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
        setManualReceptionItem({ supplier_product_id: '', quantity: 1, unit_price: '' });
    };

    const handleRemoveManualReceptionItem = (index) => {
        setManualReceptionForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const calculateManualReceptionTotal = () => {
        return manualReceptionForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    };

    // Manual Reception detail and delete handlers
    const handleViewManualReception = (reception) => {
        setSelectedManualReception(reception);
        setManualReceptionDetailDialog(true);
    };

    const handleDeleteManualReceptionClick = (reception) => {
        setManualReceptionToDelete(reception);
        setDeleteManualReceptionDialog(true);
    };

    const handleConfirmDeleteManualReception = async () => {
        if (!manualReceptionToDelete) return;
        try {
            await ordersAPI.deleteManualReception(manualReceptionToDelete.id);
            toast.success('Réception manuelle supprimée');
            setDeleteManualReceptionDialog(false);
            setManualReceptionToDelete(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const handleEditManualReception = (reception) => {
        const items = reception.items.map(item => {
            const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
            return {
                supplier_product_id: item.supplier_product_id,
                industrial_product_id: item.industrial_product_id,
                product_name: item.supplier_product_name || item.product_name || sp?.supplier_name_product || '',
                supplier_ref: item.supplier_ref || sp?.supplier_ref || '',
                industrial_product_name: item.industrial_product_name || sp?.industrial_product_name || '',
                quantity: item.quantity,
                unit_price: item.unit_price
            };
        });
        
        setEditingManualReception(reception);
        setManualReceptionForm({
            supplier_id: reception.supplier_id,
            site_id: reception.site_id,
            reception_date: reception.reception_date || reception.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            notes: reception.notes || '',
            document_number: reception.document_number || '',
            items: items
        });
        setManualReceptionDetailDialog(false);
        setManualReceptionDialog(true);
    };

    const handleDuplicateManualReception = (reception) => {
        const items = reception.items.map(item => {
            const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
            return {
                supplier_product_id: item.supplier_product_id,
                industrial_product_id: item.industrial_product_id,
                product_name: item.supplier_product_name || item.product_name || sp?.supplier_name_product || '',
                supplier_ref: item.supplier_ref || sp?.supplier_ref || '',
                industrial_product_name: item.industrial_product_name || sp?.industrial_product_name || '',
                quantity: item.quantity,
                unit_price: item.unit_price,
            };
        });

        // IMPORTANT: no editingManualReception → it will be a CREATE on save
        setEditingManualReception(null);
        setManualReceptionForm({
            supplier_id: reception.supplier_id,
            site_id: reception.site_id,
            reception_date: new Date().toISOString().split('T')[0], // today by default, user can override
            notes: reception.notes || '',
            document_number: '', // blank — user enters new BL number
            items: items,
        });
        setManualReceptionDetailDialog(false);
        setManualReceptionDialog(true);
        toast.success('Réception dupliquée — modifiez date, site, n° de pièce ou contenu puis enregistrez');
    };

    const handleSaveManualReception = async () => {
        if (!manualReceptionForm.supplier_id || !manualReceptionForm.site_id || manualReceptionForm.items.length === 0) {
            toast.error('Veuillez remplir tous les champs obligatoires et ajouter au moins un produit');
            return;
        }
        if (manualReceptionSaving) return; // anti double-clic
        setManualReceptionSaving(true);
        try {
            const data = {
                supplier_id: manualReceptionForm.supplier_id,
                site_id: manualReceptionForm.site_id,
                reception_date: manualReceptionForm.reception_date,
                notes: manualReceptionForm.notes,
                document_number: manualReceptionForm.document_number || '',
                items: manualReceptionForm.items.map(item => ({
                    supplier_product_id: item.supplier_product_id,
                    industrial_product_id: item.industrial_product_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    override_supplier_price: !!item.override_supplier_price,
                }))
            };
            
            if (editingManualReception) {
                await ordersAPI.updateManualReception(editingManualReception.id, data);
                toast.success('Réception manuelle modifiée');
            } else {
                await ordersAPI.createManualReception(data);
                toast.success('Réception manuelle créée');
            }
            
            setManualReceptionDialog(false);
            setEditingManualReception(null);
            setManualReceptionForm({ supplier_id: '', site_id: '', reception_date: new Date().toISOString().split('T')[0], notes: '', document_number: '', items: [] });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setManualReceptionSaving(false);
        }
    };

    const handleOpenManualReceptionDialog = () => {
        setEditingManualReception(null);
        setManualReceptionForm({ supplier_id: '', site_id: '', reception_date: new Date().toISOString().split('T')[0], notes: '', document_number: '', items: [] });
        setManualReceptionDialog(true);
    };

    const [fabOpen, setFabOpen] = useState(false);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="orders-tab">
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
                {canExportExcel && (
                <Button variant="outline" onClick={handleExport} data-testid="export-orders-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                </Button>
                )}
                {canCreate && (
                <Button onClick={() => setOrderFormsDialog(true)} variant="outline" data-testid="order-forms-btn">
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Formulaires
                </Button>
                )}
                {canCreate && (
                <Button onClick={() => setOrderFormPhotoDialog(true)} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="order-form-photo-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Import Commande IA
                </Button>
                )}
                {canCreate && (
                <Button onClick={() => setCreateDialog(true)} className="btn-primary" data-testid="create-order-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle commande
                </Button>
                )}
                {canCreateReception && (
                <Button onClick={handleOpenManualReceptionDialog} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="create-reception-btn">
                    <PackageCheck className="w-4 h-4 mr-2" />
                    Nouvelle réception
                </Button>
                )}
                {canCreateReception && (
                <Button onClick={() => setConsolidatedReceptionDialog(true)} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="create-consolidated-reception-btn">
                    <GitMerge className="w-4 h-4 mr-2" />
                    Réception consolidée
                </Button>
                )}
                {canImportInvoice && (
                <Button onClick={() => setInvoiceImportDialog(true)} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="invoice-import-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Import facture (IA)
                </Button>
                )}
                {canDelete && (
                <Button
                    onClick={() => setTrashDialog(true)}
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-50 relative"
                    data-testid="open-trash-btn"
                    title="Corbeille (commandes supprimées, restaurables 30 jours)"
                >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Corbeille
                    {trashCount > 0 && (
                        <Badge className="ml-2 bg-slate-200 text-slate-700 hover:bg-slate-200" data-testid="trash-count-badge">
                            {trashCount}
                        </Badge>
                    )}
                </Button>
                )}
            </div>

            {emailTestMode && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-300 rounded-lg" data-testid="email-test-mode-banner">
                    <FlaskConical className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Mode Test actif</span>
                    <span className="text-xs text-amber-600">— Les emails de commande sont redirigés vers l'adresse de test</span>
                </div>
            )}

            {/* Company + period shortcuts + Filters + list table — extracted components */}
            <OrdersFilterBar
                filterCompany={filterCompany} setFilterCompany={setFilterCompany}
                filterSite={filterSite} setFilterSite={setFilterSite}
                filterSupplier={filterSupplier} setFilterSupplier={setFilterSupplier}
                filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                filterType={filterType} setFilterType={setFilterType}
                filterPeriod={filterPeriod} setFilterPeriod={setFilterPeriod}
                customPeriodStart={customPeriodStart} setCustomPeriodStart={setCustomPeriodStart}
                customPeriodEnd={customPeriodEnd} setCustomPeriodEnd={setCustomPeriodEnd}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                sites={sites}
                suppliers={suppliers}
                excludedSites={excludedSites}
                excludedCompanies={excludedCompanies}
                onToggleExcludeSite={toggleExcludeSite}
                onToggleExcludeCompany={toggleExcludeCompany}
                onClearExclusions={clearExclusions}
                supplierShortcutNames={supplierShortcutNames}
                onEditSupplierShortcuts={() => setShowShortcutEditor(true)}
                resolveShortcutSupplier={resolveShortcutSupplier}
                normalizeStr={normalizeStr}
                periodPresets={PERIOD_PRESETS}
                companyKeys={Object.keys(COMPANY_MATCHERS)}
                allowedSites={getAllowedSites(sites)}
            />

            <OrdersListTable
                unifiedList={unifiedList}
                calculateLineTotal={calculateLineTotal}
                onOpenOrderDetail={handleOpenOrderDetail}
                onOpenReception={handleOpenReception}
                onInvoiceReception={canImportInvoice ? (order) => { setInvoiceReceptionOrder(order); setInvoiceReceptionDialog(true); } : null}
                onSendEmail={handleSendEmail}
                onDeleteOrder={handleDeleteClick}
                onViewManualReception={handleViewManualReception}
                onViewTransfer={(item) => setViewTransferItem(item)}
                onDeleteManualReception={handleDeleteManualReceptionClick}
                onStatusChange={handleStatusChange}
                onCloseRemaining={(order) => setCloseRemainingConfirmOrder(order)}
                canSendEmail={canSendEmail}
                canDelete={canDelete}
                canCreateReception={canCreateReception}
            />

            <OrdersDialogs
                suppliers={suppliers} orderCreationSuppliers={orderCreationSuppliers} sites={sites} supplierProducts={supplierProducts} industrialProducts={industrialProducts}
                availableSupplierProducts={availableSupplierProducts} availableManualReceptionProducts={availableManualReceptionProducts}
                hideAddedProducts={hideAddedProducts} setHideAddedProducts={setHideAddedProducts}
                orderableMode={orderableMode} setOrderableMode={setOrderableMode}
                manualReceptionOrderableMode={manualReceptionOrderableMode} setManualReceptionOrderableMode={setManualReceptionOrderableMode}
                receptionOrderableMode={receptionOrderableMode} setReceptionOrderableMode={setReceptionOrderableMode}
                createDialog={createDialog} setCreateDialog={wrapSetCreateDialog}
                detailDialog={detailDialog} setDetailDialog={wrapSetDetailDialog}
                deleteDialog={deleteDialog} setDeleteDialog={setDeleteDialog}
                receptionDialog={receptionDialog} setReceptionDialog={setReceptionDialog}
                editReceptionDialog={editReceptionDialog} setEditReceptionDialog={setEditReceptionDialog}
                manualReceptionDialog={manualReceptionDialog} setManualReceptionDialog={setManualReceptionDialog}
                manualReceptionDetailDialog={manualReceptionDetailDialog} setManualReceptionDetailDialog={wrapSetManualReceptionDetailDialog}
                deleteManualReceptionDialog={deleteManualReceptionDialog} setDeleteManualReceptionDialog={setDeleteManualReceptionDialog}
                selectedOrder={selectedOrder} selectedManualReception={selectedManualReception}
                orderToDelete={orderToDelete} manualReceptionToDelete={manualReceptionToDelete}
                orderForm={orderForm} setOrderForm={setOrderForm}
                currentItem={currentItem} setCurrentItem={setCurrentItem}
                addItemToOrder={addItemToOrder} setAddItemToOrder={setAddItemToOrder}
                addReceptionItem={addReceptionItem} setAddReceptionItem={setAddReceptionItem}
                receptionHistory={receptionHistory}
                editingReception={editingReception} setEditingReception={setEditingReception}
                receptionItems={receptionItems} setReceptionItems={setReceptionItems}
                extraReceptionItem={extraReceptionItem} setExtraReceptionItem={setExtraReceptionItem}
                availableExtraReceptionProducts={availableExtraReceptionProducts}
                handleAddExtraReceptionItem={handleAddExtraReceptionItem}
                replacementDialog={replacementDialog} setReplacementDialog={setReplacementDialog}
                replacementForm={replacementForm} setReplacementForm={setReplacementForm}
                handleOpenReplacement={handleOpenReplacement} handleConfirmReplacement={handleConfirmReplacement}
                closeRemainingQuantities={closeRemainingQuantities} setCloseRemainingQuantities={setCloseRemainingQuantities}
                documentNumber={documentNumber} setDocumentNumber={setDocumentNumber}
                receptionDate={receptionDate} setReceptionDate={setReceptionDate}
                manualReceptionForm={manualReceptionForm} setManualReceptionForm={setManualReceptionForm}
                manualReceptionItem={manualReceptionItem} setManualReceptionItem={setManualReceptionItem}
                editingManualReception={editingManualReception} setEditingManualReception={setEditingManualReception}
                editOrderMode={editOrderMode} setEditOrderMode={setEditOrderMode} editOrderItems={editOrderItems} setEditOrderItems={setEditOrderItems}
                editOrderNotes={editOrderNotes} setEditOrderNotes={setEditOrderNotes}
                editOrderDate={editOrderDate} setEditOrderDate={setEditOrderDate}
                editOrderDocNumber={editOrderDocNumber} setEditOrderDocNumber={setEditOrderDocNumber}
                getAllowedSites={getAllowedSites}
                handleAddItem={handleAddItem} handleRemoveItem={handleRemoveItem} handleCreateOrder={handleCreateOrder}
                handleStartEditOrder={handleStartEditOrder} handleCancelEditOrder={handleCancelEditOrder} handleAddItemToEditOrder={handleAddItemToEditOrder}
                isOrderInReceptionMode={isOrderInReceptionMode} handleAddReceptionItemDirect={handleAddReceptionItemDirect}
                handleRemoveItemFromEditOrder={handleRemoveItemFromEditOrder} handleUpdateOrderItemQuantity={handleUpdateOrderItemQuantity} handleUpdateOrderItemReceived={handleUpdateOrderItemReceived} handleUpdateOrderItemMode={handleUpdateOrderItemMode} handleSaveOrderChanges={handleSaveOrderChanges}
                handleConfirmDelete={handleConfirmDelete}
                handleSaveReception={handleSaveReception} handleSaveReceptionDraft={handleSaveReceptionDraft} hasRemainingQuantities={hasRemainingQuantities} hasUnreceivedQuantities={hasUnreceivedQuantities}
                receptionSaving={receptionSaving} manualReceptionSaving={manualReceptionSaving}
                handleEditReception={handleEditReception} handleSaveReceptionEdit={handleSaveReceptionEdit} handleDeleteReception={handleDeleteReception}
                handleAddManualReceptionItem={handleAddManualReceptionItem} handleRemoveManualReceptionItem={handleRemoveManualReceptionItem}
                calculateManualReceptionTotal={calculateManualReceptionTotal} handleSaveManualReception={handleSaveManualReception}
                handleConfirmDeleteManualReception={handleConfirmDeleteManualReception}
                handleEditManualReception={handleEditManualReception} handleDuplicateManualReception={handleDuplicateManualReception} handleDeleteManualReceptionClick={handleDeleteManualReceptionClick}
                calculateLineTotal={calculateLineTotal} calculateTotal={calculateTotal} getBillingInfo={getBillingInfo} getOrderModeLabel={getOrderModeLabel} getSupplierProductCategory={getSupplierProductCategory}
                canEdit={canEdit} canDelete={canDelete} canSendEmail={canSendEmail} canSendWhatsApp={canSendWhatsApp} canCreateReception={canCreateReception}
                handleSendEmail={handleSendEmail}
                loadData={loadData}
            />

            <OrdersTrashDialog
                open={trashDialog}
                onClose={() => setTrashDialog(false)}
                onRestored={() => { loadData(); }}
            />

            <InvoiceReceptionDialog
                open={invoiceReceptionDialog}
                onOpenChange={setInvoiceReceptionDialog}
                order={invoiceReceptionOrder}
                supplierProducts={supplierProducts}
                onDone={() => loadData()}
            />

            <TransferInViewDialog item={viewTransferItem} onOpenChange={setViewTransferItem} />

            <OrderFormsDialog
                open={orderFormsDialog}
                onOpenChange={setOrderFormsDialog}
                sites={sites}
                suppliers={suppliers}
                canEdit={user?.role === 'admin' || user?.username === 'ddultheo'}
            />
            <OrderFormPhotoImportDialog
                open={orderFormPhotoDialog}
                onOpenChange={setOrderFormPhotoDialog}
                onCreated={loadData}
                sites={sites}
            />
            <ConsolidatedManualReceptionDialog
                open={consolidatedReceptionDialog}
                onOpenChange={setConsolidatedReceptionDialog}
                suppliers={suppliers}
                sites={sites}
                supplierProducts={supplierProducts}
                industrialProducts={industrialProducts}
                onCreated={() => loadData()}
            />

            {/* Mobile FAB (Floating Action Button) - Only visible on mobile */}
            {(canCreate || canCreateReception || canImportInvoice) && (
                <div className="sm:hidden fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
                    {fabOpen && (
                        <>
                            {canImportInvoice && (
                                <button
                                    type="button"
                                    onClick={() => { setFabOpen(false); setInvoiceImportDialog(true); }}
                                    className="flex items-center gap-2 bg-orange-500 text-white px-4 py-3 rounded-full shadow-lg active:scale-95 transition-transform"
                                    data-testid="fab-invoice-import-btn"
                                >
                                    <Sparkles className="w-5 h-5" />
                                    <span className="text-sm font-medium whitespace-nowrap">Import facture (IA)</span>
                                </button>
                            )}
                            {canCreateReception && (
                                <button
                                    type="button"
                                    onClick={() => { setFabOpen(false); handleOpenManualReceptionDialog(); }}
                                    className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-3 rounded-full shadow-lg active:scale-95 transition-transform"
                                    data-testid="fab-new-reception-btn"
                                >
                                    <PackageCheck className="w-5 h-5" />
                                    <span className="text-sm font-medium whitespace-nowrap">Nouvelle réception</span>
                                </button>
                            )}
                            {canCreate && (
                                <button
                                    type="button"
                                    onClick={() => { setFabOpen(false); setCreateDialog(true); }}
                                    className="flex items-center gap-2 bg-slate-900 text-white px-4 py-3 rounded-full shadow-lg active:scale-95 transition-transform"
                                    data-testid="fab-new-order-btn"
                                >
                                    <ShoppingCart className="w-5 h-5" />
                                    <span className="text-sm font-medium whitespace-nowrap">Nouvelle commande</span>
                                </button>
                            )}
                        </>
                    )}
                    <button
                        type="button"
                        onClick={() => setFabOpen(!fabOpen)}
                        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all active:scale-95 ${fabOpen ? 'bg-red-500 rotate-45' : 'bg-slate-900'} text-white`}
                        data-testid="fab-toggle-btn"
                        aria-label={fabOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                    >
                        <Plus className="w-7 h-7" />
                    </button>
                </div>
            )}
            {/* Backdrop for FAB */}
            {fabOpen && (
                <button
                    type="button"
                    aria-label="Fermer le menu"
                    className="sm:hidden fixed inset-0 bg-black/20 z-30"
                    onClick={() => setFabOpen(false)}
                />
            )}

            <CarrefourImportDialog
                open={carrefourDialog}
                onClose={() => setCarrefourDialog(false)}
                sites={sites}
                onImported={loadData}
            />

            <InvoiceImportDialog
                open={invoiceImportDialog}
                onClose={() => setInvoiceImportDialog(false)}
                sites={sites}
                suppliers={suppliers}
                onImported={loadData}
            />

            <SupplierShortcutEditor
                open={showShortcutEditor}
                onClose={() => setShowShortcutEditor(false)}
                suppliers={suppliers}
                shortcutNames={supplierShortcutNames}
                onChange={setSupplierShortcutNames}
                defaults={DEFAULT_SUPPLIER_SHORTCUTS}
                resolveShortcutSupplier={resolveShortcutSupplier}
            />

            {/* Confirmation de cloture manuelle des quantites restantes */}
            <AlertDialog open={!!closeRemainingConfirmOrder} onOpenChange={(o) => !o && setCloseRemainingConfirmOrder(null)}>
                <AlertDialogContent data-testid="close-remaining-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Clôturer les quantités restantes ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            La commande <strong>{closeRemainingConfirmOrder?.order_number}</strong> ({closeRemainingConfirmOrder?.supplier_name}) passera au statut <strong>« livrée »</strong>. Les lignes non entièrement reçues seront closes sans créer de mouvement de stock supplémentaire.
                            <br /><br />
                            Cette action est <strong>irréversible</strong>. Une note horodatée sera ajoutée à la commande.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="close-remaining-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => handleCloseRemainingQuantities(closeRemainingConfirmOrder?.id)}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="close-remaining-confirm-btn"
                        >
                            Oui, clôturer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Garde-fou : au moment d'enregistrer une reception incomplete */}
            <AlertDialog open={!!pendingReceptionSave} onOpenChange={(o) => !o && setPendingReceptionSave(null)}>
                <AlertDialogContent data-testid="incomplete-reception-guard">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Réception incomplète</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div>
                                <p>Il reste <strong>{pendingReceptionSave?.incompleteLines?.length || 0}</strong> ligne(s) non entièrement reçue(s) :</p>
                                <ul className="mt-2 mb-3 list-disc list-inside text-sm max-h-40 overflow-y-auto">
                                    {(pendingReceptionSave?.incompleteLines || []).slice(0, 8).map((l, idx) => (
                                        <li key={idx}>{l.name} — reste <strong>{Number(l.remaining).toFixed(2)} {l.unit}</strong></li>
                                    ))}
                                    {(pendingReceptionSave?.incompleteLines || []).length > 8 && (
                                        <li className="text-slate-500">... et {(pendingReceptionSave?.incompleteLines || []).length - 8} autre(s)</li>
                                    )}
                                </ul>
                                <p>Voulez-vous <strong>clôturer</strong> ces quantités (statut « livrée ») ou <strong>laisser la commande ouverte</strong> (statut « partiellement reçue ») pour la compléter plus tard ?</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel data-testid="incomplete-cancel">Annuler la saisie</AlertDialogCancel>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-slate-300 bg-white px-4 py-2 hover:bg-slate-50"
                            onClick={() => {
                                const pr = pendingReceptionSave;
                                setPendingReceptionSave(null);
                                if (pr) performReceptionSave(pr.itemsToReceive, false);
                            }}
                            data-testid="incomplete-leave-open-btn"
                        >
                            Laisser ouverte
                        </button>
                        <AlertDialogAction
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => {
                                const pr = pendingReceptionSave;
                                setPendingReceptionSave(null);
                                if (pr) {
                                    setCloseRemainingQuantities(true);
                                    performReceptionSave(pr.itemsToReceive, true);
                                }
                            }}
                            data-testid="incomplete-close-btn"
                        >
                            Oui, clôturer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
